import type {
  AppealAmountBasis,
  AppealFavours,
  AppealIssueDisposition,
  AppealOutcome,
  AppealSupports,
  AppellantKind,
  CitedBy,
} from "../types"

/**
 * Colour and polarity words for an appeal.
 *
 * WHY THIS MODULE EXISTS: "appeal allowed" means opposite things in a
 * taxpayer's appeal and in a departmental appeal (design section 6; frontend
 * map G10). A green "Allowed" pill on a department's appeal would tell the
 * officer the taxpayer won when the taxpayer lost. So:
 *
 *  - Colour follows the EFFECT ON THE DEMAND OR REFUND, i.e. on the taxpayer:
 *    `favour` (green) = the taxpayer is relieved (demand reduced, refund
 *    restored); `upheld` (brick) = the burden on the taxpayer stands or grows;
 *    `partial` (amber) = both; `neutral` = no money effect / not decided.
 *    These are the OIO tokens with the OIO meaning ("dropped" is green), so one
 *    colour means one thing across the product.
 *  - Every coloured label also NAMES THE PARTY in words ("favours the
 *    taxpayer (appellant)"), because colour alone is ambiguous and invisible
 *    to some officers.
 *  - Appeal roles (appellant / respondent) are mapped to sides (taxpayer /
 *    department) through the appellant kind; never assume the appellant is the
 *    taxpayer.
 */

/**
 * `open` is not a polarity. The other four say who a decided point favours;
 * `open` says the point is NOT decided — the officer still owes it a
 * direction and reasons. It reads as attention rather than outcome, so it is
 * never mistaken for a result and never blends into `neutral`, which means
 * "decided, favours nobody".
 */
export type Tone = "favour" | "upheld" | "partial" | "neutral" | "open"
export type Party = "taxpayer" | "department"
export type AppealRole = "appellant" | "respondent"

/** Which side holds an appeal role in this appeal. */
export function partyForRole(role: AppealRole, appellantKind: AppellantKind): Party {
  if (role === "appellant") return appellantKind === "department" ? "department" : "taxpayer"
  return appellantKind === "department" ? "taxpayer" : "department"
}

export function roleForParty(party: Party, appellantKind: AppellantKind): AppealRole {
  return partyForRole("appellant", appellantKind) === party ? "appellant" : "respondent"
}

/** "the taxpayer (appellant)" / "the department (respondent)". */
export function partyPhrase(party: Party, appellantKind: AppellantKind): string {
  return `the ${party} (${roleForParty(party, appellantKind)})`
}

/** Tone of something that favours a side. */
export function toneForParty(party: Party | "both" | "neither"): Tone {
  if (party === "taxpayer") return "favour"
  if (party === "department") return "upheld"
  if (party === "both") return "partial"
  return "neutral"
}

// ---------------------------------------------------------------------------
// Outcome of the appeal
// ---------------------------------------------------------------------------
export const OUTCOME_LABEL: Record<AppealOutcome, string> = {
  appeal_allowed: "Appeal allowed",
  appeal_partly_allowed: "Appeal partly allowed",
  appeal_rejected: "Appeal rejected",
  rejected_time_barred: "Rejected as time-barred",
  rejected_predeposit: "Rejected for want of pre-deposit",
  dismissed_as_withdrawn: "Dismissed as withdrawn",
  returned_want_of_jurisdiction: "Returned for want of jurisdiction",
  dept_appeal_allowed: "Department's appeal allowed",
  dept_appeal_partly_allowed: "Department's appeal partly allowed",
  dept_appeal_rejected: "Department's appeal rejected",
  // This order disposes of nothing: every point before it is still open. A
  // draft state — it delivers so the officer is never left with nothing, and
  // the provisional list keeps it unsignable.
  not_disposed: "Not disposed — points still open",
}

/** Which appeal role an outcome favours ("mixed" = partly allowed). */
function roleFavouredBy(outcome: AppealOutcome): AppealRole | "mixed" | "neither" | "open" {
  switch (outcome) {
    case "appeal_allowed":
    case "dept_appeal_allowed":
      return "appellant"
    case "appeal_partly_allowed":
    case "dept_appeal_partly_allowed":
      return "mixed"
    case "appeal_rejected":
    case "rejected_time_barred":
    case "rejected_predeposit":
    case "dept_appeal_rejected":
      return "respondent"
    case "dismissed_as_withdrawn":
    case "returned_want_of_jurisdiction":
      return "neither"
    case "not_disposed":
      // Not "neither": nobody won because nothing was decided. The caller
      // renders it with the `open` tone, as an action rather than a result.
      return "open"
  }
}

export interface PolarityView {
  label: string
  /** Words naming the party, e.g. "favours the taxpayer (appellant)". */
  partyText: string
  tone: Tone
}

export function outcomeView(outcome: AppealOutcome, appellantKind: AppellantKind): PolarityView {
  const role = roleFavouredBy(outcome)
  const label = OUTCOME_LABEL[outcome] ?? outcome
  if (role === "mixed") {
    return {
      label,
      partyText: `in part for ${partyPhrase("taxpayer", appellantKind)}, in part for ${partyPhrase("department", appellantKind)}`,
      tone: "partial",
    }
  }
  if (role === "neither") {
    return { label, partyText: "no decision on the merits; the order below stands", tone: "neutral" }
  }
  if (role === "open") {
    // Deliberately NOT "the order below stands": nothing was determined, so
    // the amounts are neither confirmed nor set aside, and saying the order
    // below stands would state the very thing this order declined to decide.
    return {
      label,
      partyText: "nothing is determined; the amounts in dispute are neither confirmed nor set aside",
      tone: "open",
    }
  }
  const party = partyForRole(role, appellantKind)
  return { label, partyText: `favours ${partyPhrase(party, appellantKind)}`, tone: toneForParty(party) }
}

// ---------------------------------------------------------------------------
// One issue's decision
// ---------------------------------------------------------------------------
export const DISPOSITION_LABEL: Record<AppealIssueDisposition, string> = {
  ground_accepted: "Ground accepted",
  ground_rejected: "Ground rejected",
  ground_partly_accepted: "Ground partly accepted",
  defect_held: "Defect held",
  defect_not_held: "Defect not held",
  not_pressed: "Not pressed",
  infructuous: "Infructuous",
  // Display words, never the machine spelling: the printed order bans engine
  // tokens (gate OIA-G13 rejects any rendered `snake_case` disposition).
  not_determined: "Not determined",
}

/** `favours` is computed by the backend by appeal role; name it by side. */
export function favoursView(favours: AppealFavours, appellantKind: AppellantKind): PolarityView {
  if (favours === "neither") return { label: "No effect", partyText: "favours neither party", tone: "neutral" }
  const party = partyForRole(favours, appellantKind)
  return {
    label: favours === "appellant" ? "For the appellant" : "For the respondent",
    partyText: `favours ${partyPhrase(party, appellantKind)}`,
    tone: toneForParty(party),
  }
}

/**
 * Tone for a disposition when the backend's `favours` is not at hand (a card's
 * drafted disposition). Ground verbs are relative to the APPELLANT's ground;
 * suo motu defect verbs can cut either way, so they stay neutral rather than
 * guess (backend decisions.py: "counting a defect held as a win for the
 * appellant because the verb sounds like one is how the outcome goes wrong").
 */
export function dispositionTone(disposition: AppealIssueDisposition | null, appellantKind: AppellantKind): Tone {
  switch (disposition) {
    case "ground_accepted":
      return toneForParty(partyForRole("appellant", appellantKind))
    case "ground_rejected":
      return toneForParty(partyForRole("respondent", appellantKind))
    case "ground_partly_accepted":
      return "partial"
    case "not_determined":
      // An open point is an action, not an outcome. `neutral` would read as
      // "decided, favours neither party" — the opposite of what it means.
      return "open"
    default:
      return "neutral"
  }
}

// ---------------------------------------------------------------------------
// Money effect
// ---------------------------------------------------------------------------
/**
 * Whether a LOWER sustained figure helps the taxpayer. Mirrors
 * `AppealAmountBasis.reduction_favours_taxpayer`: false only for a sanctioned
 * refund (a departmental refund appeal), where reducing the figure takes
 * refund away from the taxpayer.
 */
export function reductionFavoursTaxpayer(basis: AppealAmountBasis): boolean {
  return basis !== "refund_sanctioned"
}

export const BASIS_LABEL: Record<AppealAmountBasis, { impugned: string; sustained: string; setAside: string }> = {
  demand_confirmed: {
    impugned: "Demand confirmed below",
    sustained: "Demand sustained",
    setAside: "Demand set aside",
  },
  refund_rejected: {
    impugned: "Refund rejected below",
    sustained: "Rejection sustained",
    setAside: "Rejection set aside (refund allowed)",
  },
  refund_sanctioned: {
    impugned: "Refund sanctioned below",
    sustained: "Sanction sustained",
    setAside: "Sanction set aside (refund reduced)",
  },
  penalty_imposed: {
    impugned: "Penalty imposed below",
    sustained: "Penalty sustained",
    setAside: "Penalty set aside",
  },
}

/** Tone and words for a change in a sustained figure (after − before). */
export function amountChangeView(delta: number | null, basis: AppealAmountBasis): PolarityView {
  if (delta === null || delta === 0) {
    return { label: "No change", partyText: "no change in the amounts", tone: "neutral" }
  }
  const relievesTaxpayer = delta < 0 === reductionFavoursTaxpayer(basis)
  const party: Party = relievesTaxpayer ? "taxpayer" : "department"
  let label: string
  switch (basis) {
    case "refund_rejected":
      label = delta < 0 ? "More refund to the taxpayer" : "Less refund to the taxpayer"
      break
    case "refund_sanctioned":
      label = delta < 0 ? "Reduces the taxpayer's refund" : "Restores refund to the taxpayer"
      break
    case "penalty_imposed":
      label = delta < 0 ? "Penalty on the taxpayer reduced" : "Penalty on the taxpayer increased"
      break
    default:
      label = delta < 0 ? "Demand on the taxpayer reduced" : "Demand on the taxpayer increased"
  }
  return { label, partyText: `favours the ${party}`, tone: toneForParty(party) }
}

// ---------------------------------------------------------------------------
// Authorities
// ---------------------------------------------------------------------------
/** `supports` is by SIDE already. */
export function supportsView(supports: AppealSupports): PolarityView {
  switch (supports) {
    case "taxpayer":
      return { label: "Supports the taxpayer", partyText: "for the taxpayer", tone: "favour" }
    case "revenue":
      return { label: "Supports the department", partyText: "for the department (revenue)", tone: "upheld" }
    case "neutral":
      return { label: "Neutral", partyText: "for neither side", tone: "neutral" }
    default:
      return { label: "Unclear", partyText: "side not settled", tone: "neutral" }
  }
}

/** Who cited it, named by role AND side. */
export function citedByLabel(citedBy: CitedBy, appellantKind: AppellantKind): string {
  switch (citedBy) {
    case "appellant":
      return `Appellant (${partyForRole("appellant", appellantKind)})`
    case "respondent":
      return `Respondent (${partyForRole("respondent", appellantKind)})`
    case "adjudicating_authority":
      return "Order below"
    case "department":
      return "Department"
    case "officer":
      return "You"
    case "system":
      return "Found by NitiAssist"
  }
}
